// @flow
import {createSelector} from 'reselect';

import type {ApplicationStoreType} from 'constants/flow/flowTypes';
import type {
    ShippingMethodType,
    PaymentDetailsLineType,
    ShippingOptionsLineType,
    PaymentDetailsType,
} from 'constants/flow/flowTypesCheckout';
import type {ProductType} from 'constants/flow/flowTypesProduct';
import {APPLE_PAY_LABELS, APPLE_PAY_DEFAULT_SHIPPING_OPTIONS} from 'constants/applePay/applePay';

import {getCurrentPage} from 'selectors/app/app';
import type {CheckoutType} from 'reducers/checkout/checkoutType';
import {getQuoteItems, getCartTotals} from 'selectors/cart/cart';
import {getCheckoutData} from 'selectors/checkout/checkout';
import {getPaymentRequestOptions} from 'selectors/payments/payments';
import type {ApplePayTotalsType} from 'reducers/applePayTotals/applePayTotalsType';
import {getOrderReviewItems as getOrderReviewItemsSelector} from 'selectors/checkout/orderReview/orderReview';
import {floatPriceFromFormated} from 'libs/convertPrice/formatPrice';

import {paymentDetailsItemStructure} from './helpers';

/**
 * Returns shipping options for payment request
 * @param {ApplicationStoreType} store - Current store
 * @returns {Array<ShippingOptionsLineType>} - shipping options in structure for payment request
 */
export const getShippingOptions = (store: ApplicationStoreType): Array<ShippingOptionsLineType> => {
    const methods = store.shippingMethod.list.length
        ? store.shippingMethod.list
        : APPLE_PAY_DEFAULT_SHIPPING_OPTIONS;
    return methods.map(({code, price, title, description, selected}: ShippingMethodType) => ({
        id: code,
        detail: description,
        selected,
        ...paymentDetailsItemStructure(title, price),
    }));
};

/**
 * Collects data about items for payment request details
 * @param {ApplicationStoreType} store - Current store
 * @returns {Array<PaymentDetailsLineType>} - items for payment request details
 */
export const getPaymentDetailsItems = createSelector(
    getCurrentPage,
    getQuoteItems,
    getOrderReviewItemsSelector,
    (
        {page}: {page: string},
        quoteItems: Array<ProductType>,
        orderReviewItems: Array<ProductType>,
    ): Array<PaymentDetailsLineType> => {
        const items = page === 'checkout' ? orderReviewItems : quoteItems;
        return items.map(({name, total}: ProductType) => paymentDetailsItemStructure(name, total));
    },
);

/**
 * Returns data about apple pay totals
 * note: if grandTotal is 0 it will be return total from quote totals
 * @param {ApplicationStoreType} store - Current store
 * @returns {ApplePayTotalsType} - apple pay totals
 */
export const getApplePayTotals = createSelector(
    getCartTotals,
    store => store.applePayTotals,
    ({total}: {total: number}, applePayTotals: ApplePayTotalsType): ApplePayTotalsType => ({
        ...applePayTotals,
        grandTotal: floatPriceFromFormated(applePayTotals.grandTotal || total),
    }),
);

/**
 * Collects payment request details including shipping options
 * @param {ApplicationStoreType} store - Current store
 * @returns {PaymentDetailsType} - payment details info for payment request with shipping data included
 */
export const getPaymentDetailsWithShipping = createSelector(
    getApplePayTotals,
    getPaymentDetailsItems,
    getShippingOptions,
    (
        {
            discount,
            grandTotal,
            taxTotal = 0,
            shipping: shippingPrice,
            shippingDiscount = 0,
        }: ApplePayTotalsType,
        items: Array<PaymentDetailsLineType>,
        shippingOptions: Array<ShippingOptionsLineType>,
    ): PaymentDetailsType => {
        let displayItems = items;
        if (Number(discount))
            displayItems = [
                ...displayItems,
                paymentDetailsItemStructure(
                    APPLE_PAY_LABELS.DISCOUNT,
                    `-${Number(discount) - Number(shippingDiscount)}`,
                ),
            ];
        displayItems = [
            ...displayItems,
            paymentDetailsItemStructure(APPLE_PAY_LABELS.SHIPPING, shippingPrice),
        ];
        if (Number(shippingDiscount))
            displayItems = [
                ...displayItems,
                paymentDetailsItemStructure(
                    APPLE_PAY_LABELS.SHIPPING_DISCOUNT,
                    `-${shippingDiscount}`,
                ),
            ];
        displayItems = [
            ...displayItems,
            paymentDetailsItemStructure(APPLE_PAY_LABELS.TAX, taxTotal),
        ];
        return {
            total: paymentDetailsItemStructure(APPLE_PAY_LABELS.TOTAL, grandTotal),
            displayItems: [...displayItems],
            shippingOptions,
        };
    },
);

/**
 * Collects payment request details without shipping options
 * @param {ApplicationStoreType} store - Current store
 * @returns {PaymentDetailsType} - payment details info for payment request, shipping data is not included
 */
export const getPaymentDetailsWithoutShipping = createSelector(
    getCheckoutData,
    getPaymentDetailsItems,
    (
        {totals: {total, shippingPrice, taxTotal = 0, discount}}: CheckoutType,
        items: Array<PaymentDetailsLineType>,
    ): PaymentDetailsType => {
        let displayItems = [
            ...items,
            paymentDetailsItemStructure(APPLE_PAY_LABELS.SHIPPING, shippingPrice),
        ];
        if (Number(discount))
            displayItems = [
                ...displayItems,
                paymentDetailsItemStructure(APPLE_PAY_LABELS.DISCOUNT, `-${discount}`),
            ];
        displayItems = [
            ...displayItems,
            paymentDetailsItemStructure(APPLE_PAY_LABELS.TAX, taxTotal),
        ];
        return {
            total: paymentDetailsItemStructure(APPLE_PAY_LABELS.TOTAL, total),
            displayItems,
        };
    },
);

/**
 * Defines which payment request details is needed and returns it
 * @param {ApplicationStoreType} store - Current store
 * @returns {PaymentDetailsType} - payment request details with/without shipping options
 */
export const getPaymentDetails = createSelector(
    getPaymentRequestOptions,
    getPaymentDetailsWithoutShipping,
    getPaymentDetailsWithShipping,
    (
        {requestShipping}: {requestShipping: boolean},
        detailsWithoutShipping: PaymentDetailsType,
        detailsWithShipping: PaymentDetailsType,
    ): PaymentDetailsType => (requestShipping ? detailsWithShipping : detailsWithoutShipping),
);
